'use client';

import { useEffect, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';

import { OAuthButtons } from '@/components/OAuthButtons';
import { SiteLogoWide } from '@/components/SiteLogoWide';
import { useUserInfo } from '@/context/UserInfoContext';
import { API_URL } from '@/lib/consts';
import { OAuthIntent } from '@/lib/oauth';

enum LoginState {
  // On page load, no session established
  NotLoggedIn,

  // Sent credentials to backend, waiting for response
  // validating those credentials
  SendingCredentials,

  // Credentials validated, waiting for session to be created
  // on the backend and for auth requests to succeed
  WaitingForSession,

  // Backend is returning that the user cookie is valid
  SessionEstablished,

  // We've requested a redirect
  Redirecting,
}

interface LoginFormProps {
  alreadyLoggedIn: boolean;
}

// TODO(1): we are still getting failure-to-redirect sometimes.
const LoginForm: React.FC<LoginFormProps> = ({ alreadyLoggedIn }) => {
  const auth_check_url = `${API_URL}/auth/check`;

  const router = useRouter();
  const [loginState, setLoginState] = useState<LoginState>(LoginState.NotLoggedIn);
  const [loginErrorMsg, setLoginErrorMsg] = useState<string>('');

  const { userInfo, reloadUserInfo } = useUserInfo();

  // Announce login state changes to screen readers
  // TODO(2): Not clear if this really should be done, we don't announce the login
  // state to non-screen-reader users...
  useEffect(() => {
    const message =
      loginState === LoginState.NotLoggedIn
        ? 'Login form ready'
        : loginState === LoginState.SendingCredentials
          ? 'Sending login credentials'
          : loginState === LoginState.WaitingForSession
            ? 'Waiting for session'
            : loginState === LoginState.SessionEstablished
              ? 'Session established'
              : loginState === LoginState.Redirecting
                ? 'Redirecting to home page'
                : 'Unknown login state';

    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    Object.assign(announcer.style, visuallyHidden);
    announcer.textContent = message;
    document.body.appendChild(announcer);

    return () => {
      document.body.removeChild(announcer);
    };
  }, [loginState]);

  // Poll for session validation
  const [pollInterval, setPollInterval] = useState<number>(200); // Start value
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (loginState === LoginState.WaitingForSession) {
      intervalId = setInterval(async () => {
        try {
          const res = await fetch(auth_check_url, { credentials: 'include' });
          if (res.ok && (await res.json()).valid) {
            setLoginState(LoginState.SessionEstablished);
          } else {
            setPollInterval((prev) => Math.min(prev * 1.6, 5000));
          }
        } catch (error) {
          setPollInterval((prev) => Math.min(prev * 1.6, 5000));
        }
      }, pollInterval); // Every this many ms
    }

    // Cleanup
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [loginState, auth_check_url, pollInterval]);

  // Get redirect path from URL parameters
  const searchParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : ''
  );
  const redirectPath = searchParams.get('redirect');

  // First effect: reload user info after session is established
  useEffect(() => {
    if (loginState === LoginState.SessionEstablished) {
      reloadUserInfo();
    }
  }, [loginState, reloadUserInfo]);

  // Second effect: handle redirect once we have user info.  We show a welcome
  // page if the user hasn't seen it yet, except that if the user requested a
  // specific redirect, we do that instead (this should be rare, and unharmful
  // since the user has a chance to see it next time they log in -- although it
  // might be a bit confusing).
  useEffect(() => {
    if (loginState === LoginState.SessionEstablished && userInfo) {
      // console.log('Have user info, redirecting', userInfo);
      setLoginState(LoginState.Redirecting);
      if (redirectPath) {
        router.push(redirectPath);
      } else {
        router.push('/');
      }
      router.refresh();
    }
  }, [loginState, userInfo, redirectPath, router]);

  const handleLoginSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoginState(LoginState.SendingCredentials);
    setLoginErrorMsg('');

    const formData = new FormData(event.currentTarget);

    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          username: formData.get('username'),
          password: formData.get('password'),
        }),
      });

      if (res.ok) {
        // Do nothing here; a useEffect will redirect us to the home page after
        // the backend is able to validate the cookie.
        setLoginState(LoginState.WaitingForSession);
      } else {
        console.log('auth request fail', res);
        const errorData = await res.json();
        setLoginErrorMsg(errorData.message || 'Login failed. Please try again.');
        setLoginState(LoginState.NotLoggedIn);
      }
    } catch (error) {
      console.log('auth request error', error);
      setLoginErrorMsg('An error occurred. Please try again.');
      setLoginState(LoginState.NotLoggedIn);
    }
  };

  if (alreadyLoggedIn) {
    return (
      <section>
        <h2>You are already logged in!</h2>
        <Typography variant="body1" sx={{ mt: 2 }}>
          <Link href="/">Go to home page</Link>
        </Typography>
        {/* TODO(2): logout button */}
      </section>
    );
  }

  return (
    // method="POST" shouldn't be required for a javascript handler, but we've
    // seen credentials occasionally appear in the URL bar (usually when there's
    // some backend problem) so we added this to see if it helps.
    <Box component="form" method="POST" onSubmit={handleLoginSubmit} aria-label="Login form">
      <Stack spacing={3}>
        <TextField
          id="username"
          name="username"
          label="Username or email"
          required
          fullWidth
          variant="outlined"
          aria-required="true"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          inputProps={{
            autoCapitalize: 'none',
            autoCorrect: 'off',
            spellCheck: false,
          }}
        />
        <TextField
          id="password"
          name="password"
          label="Password"
          type="password"
          required
          fullWidth
          variant="outlined"
          aria-required="true"
        />
        <Typography variant="body2" sx={{ textAlign: 'right' }}>
          <Link href="/reset-password/request">Forgot password?</Link>
        </Typography>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loginState !== LoginState.NotLoggedIn}
          aria-disabled={loginState !== LoginState.NotLoggedIn}
        >
          Login
        </Button>
        {loginState !== LoginState.NotLoggedIn && (
          <Alert severity="info" aria-live="polite" aria-atomic="true">
            {loginState === LoginState.SendingCredentials
              ? 'Logging in...'
              : loginState === LoginState.WaitingForSession
                ? 'Redirecting...'
                : loginState === LoginState.SessionEstablished
                  ? 'You are already logged in!'
                  : loginState === LoginState.Redirecting
                    ? 'Redirecting...'
                    : 'unknown login state'}
          </Alert>
        )}
        {loginErrorMsg && (
          <Alert severity="error" role="alert">
            {loginErrorMsg}
          </Alert>
        )}
        {/* <Typography variant="body2" sx={{ textAlign: 'center' }}>
          Don&apos;t have an account?{' '}
          <Link href="/register" style={{ color: 'inherit' }}>
            Register
          </Link>
        </Typography> */}
      </Stack>
    </Box>
  );
};

const Home: React.FC = () => {
  const alreadyLoggedIn = false; // TODO(1): check if already logged in

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <SiteLogoWide heightPx={48} href={null} />
          </Box>
          <LoginForm alreadyLoggedIn={alreadyLoggedIn} />
          {!alreadyLoggedIn && <OAuthButtons intent={OAuthIntent.Login} />}
        </CardContent>
      </Card>
    </Container>
  );
};

export default Home;
